// utils/territoryHelper.js
import { Country, City } from "country-state-city";

// In-memory caches to reduce repeated lookups
const countryCache = new Map(); // key: "US,CA,MX" -> [{ name, isoCode }]
const cityCache = new Map();    // key: "US,CA" -> ["New York", "Toronto", ...]

// Region -> Country ISO mapping.
export const REGION_TO_COUNTRY_ISO = {
  "North America": ["US", "CA", "MX"],
  "South America": ["BR", "AR", "CL", "CO", "PE"],
  Europe: ["FR", "DE", "ES", "IT", "GB", "NL", "SE", "NO", "FI", "PL"],
  Asia: ["CN", "IN", "PK", "JP", "SG", "TH", "ID", "MY", "BD", "PH", "AE", "SA"],
  Africa: ["ZA", "NG", "EG", "KE", "MA", "TZ", "UG", "DZ"],
  Oceania: ["AU", "NZ", "FJ", "PG"],
};

/**
 * Given region names (e.g., ["North America", "Europe"]),
 * returns a list of { name, isoCode } countries from country-state-city.
 */
export function getCountriesByRegions(regions = []) {
  const isoSet = new Set();
  regions.forEach((r) => {
    const arr = REGION_TO_COUNTRY_ISO[r] || [];
    arr.forEach((iso) => isoSet.add(iso));
  });

  const key = Array.from(isoSet).sort().join(",");
  if (countryCache.has(key)) return countryCache.get(key);

  const all = Country.getAllCountries() || []; // [{ name, isoCode, ... }]
  const list = all
    .filter((c) => isoSet.has(c.isoCode))
    .map((c) => ({ name: c.name, isoCode: c.isoCode }))
    .sort((a, b) => a.name.localeCompare(b.name));

  countryCache.set(key, list);
  return list;
}

/**
 * Given country NAMES (e.g., ["United States", "Canada"]),
 * returns a unique, sorted list of city names (sync).
 */
export function getCitiesByCountryNames(countryNames = []) {
  const allCountries = Country.getAllCountries() || [];
  const isoCodes = countryNames
    .map((n) => allCountries.find((c) => c.name === n)?.isoCode)
    .filter(Boolean);

  const key = isoCodes.slice().sort().join(",");
  if (cityCache.has(key)) return cityCache.get(key);

  const cityNames = [];
  isoCodes.forEach((iso) => {
    const list = City.getCitiesOfCountry(iso) || [];
    list.forEach((c) => {
      if (c?.name) cityNames.push(c.name);
    });
  });

  const uniqueSorted = Array.from(new Set(cityNames)).sort((a, b) =>
    a.localeCompare(b)
  );
  cityCache.set(key, uniqueSorted);
  return uniqueSorted;
}

/**
 * Optional: async wrapper so UI can show a loader consistently
 * without blocking the main thread, even though the library is sync.
 */
export function getCitiesByCountryNamesAsync(countryNames = []) {
  return new Promise((resolve) => {
    // Let React paint first; then compute.
    setTimeout(() => {
      resolve(getCitiesByCountryNames(countryNames));
    }, 0);
  });
}

